import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { ImageOff } from 'lucide-react';
import { fetchRoadCodeMedia } from '../../api/roadCode';
import type { RoadCodeMedia } from '../../lib/roadCode';
import { cx } from '../../components/charte';

// Illustrations du code : route protégée, lue en blob puis gardée pour la session (une question revue
// ou la suivante, préchargée, s'affichent sans attente).

const MEDIA_KEY = 'road-code-media';

async function loadMedia(file: string): Promise<string> {
  return URL.createObjectURL(await fetchRoadCodeMedia(file));
}

const mediaQuery = (file: string) => ({
  queryKey: [MEDIA_KEY, file],
  queryFn: () => loadMedia(file),
  staleTime: Infinity,
  gcTime: 30 * 60_000,
  retry: 1,
});

/** Libère les adresses locales des illustrations sorties du cache. */
function useRevokeOnEviction() {
  const client = useQueryClient();
  useEffect(
    () =>
      client.getQueryCache().subscribe((event) => {
        const local = event.query.state.data;
        if (event.type === 'removed' && event.query.queryKey[0] === MEDIA_KEY && typeof local === 'string') URL.revokeObjectURL(local);
      }),
    [client],
  );
}

/** Précharge une illustration (question suivante). */
export function usePrefetchMedia(media: RoadCodeMedia | null | undefined) {
  const client = useQueryClient();
  useEffect(() => {
    if (media) void client.prefetchQuery(mediaQuery(media.file));
  }, [client, media]);
}

/** Photo ou vidéo d'une question ou d'une fiche, au format 16/9, avec sa description. */
export function RoadCodeMediaView({ media, className }: { media: RoadCodeMedia; className?: string }) {
  useRevokeOnEviction();
  const { data: src, isError } = useQuery(mediaQuery(media.file));
  return (
    <div className={cx('relative aspect-video w-full overflow-hidden bg-surface-2', className)}>
      {src && media.kind === 'image' && <img src={src} alt={media.alt} className="h-full w-full object-cover" />}
      {src && media.kind === 'video' && (
        <video src={src} aria-label={media.alt} className="h-full w-full object-cover" controls playsInline muted autoPlay />
      )}
      {!src && !isError && <div className="absolute inset-0 animate-pulse bg-surface-2" aria-label="Chargement de l’illustration" />}
      {isError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 p-4 text-center text-[13px] text-secondary">
          <ImageOff size={20} aria-hidden />
          <span>{media.alt}</span>
        </div>
      )}
    </div>
  );
}
